import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootFooterNavProps, TootFooterLink } from "./TootFooterNav.props";

const getSafeRel = (target?: string) => (target === "_blank" ? "noopener noreferrer" : undefined);

const getPhoneHref = (phone: string) => `tel:${phone.replace(/[^+\d]/g, "")}`;

const FooterLink = ({ link, className }: { link: TootFooterLink; className?: string }) => {
  const Link = resolveTootPrimitive("Link");
  const linkClassName = cn(
    "group inline-flex min-h-11 items-center gap-2 rounded-[0.125rem] px-2 text-[0.72rem] font-extrabold uppercase leading-none tracking-[0.16em] transition-[background-color,border-color,color,transform] duration-150 ease-out md:min-h-9",
    "text-toot-muted hover:-translate-y-px hover:bg-secondary hover:text-toot-orange",
    className,
  );

  const content = (
    <>
      <span
        aria-hidden="true"
        className="h-px w-3 shrink-0 bg-current opacity-45 transition-[width,opacity] duration-150 ease-out group-hover:w-5 group-hover:opacity-80"
      />
      <span className="[overflow-wrap:anywhere]">{link.text}</span>
    </>
  );

  return (
    <Link
      aria-label={link.ariaLabel}
      className={linkClassName}
      href={link.href}
      rel={getSafeRel(link.target)}
      target={link.target}
      title={link.ariaLabel}
    >
      {content}
    </Link>
  );
};

export const TootFooterNav = ({
  className,
  contact,
  copyright,
  description,
  groups = [],
  legalLinks = [],
  logo,
  socialLinks = [],
}: ITootFooterNavProps) => {
  const Image = resolveTootPrimitive("Image");
  const Link = resolveTootPrimitive("Link");
  const footerGroups = groups ?? [];
  const footerLegalLinks = legalLinks ?? [];
  const footerSocialLinks = socialLinks ?? [];
  const derivedContactSnippets = [
    ...(contact?.email
      ? [{ label: "Email", value: contact.email, href: `mailto:${contact.email}` }]
      : []),
    ...(contact?.phone
      ? [{ label: "Phone", value: contact.phone, href: getPhoneHref(contact.phone) }]
      : []),
    ...(contact?.address ? [{ label: "Address", value: contact.address }] : []),
  ];
  const hasBrand = Boolean(logo || description || derivedContactSnippets.length > 0);
  const hasNav = footerGroups.length > 0;
  const needsTwoColumnFiller = footerGroups.length % 2 !== 0;
  const threeColumnEmptyCells = footerGroups.length > 2 ? (3 - (footerGroups.length % 3)) % 3 : 0;
  const hasLower = Boolean(footerLegalLinks.length || footerSocialLinks.length || copyright);
  const logoCaption = logo?.caption;
  const logoStyle: CSSProperties | undefined = logo?.focalPoint
    ? { objectPosition: `${logo.focalPoint.x * 100}% ${logo.focalPoint.y * 100}%` }
    : undefined;

  if (!hasBrand && !hasNav && !hasLower) return null;

  return (
    <div className={cn("toot-container py-10 text-toot-foreground sm:py-12 lg:py-16", className)}>
      <div className="relative overflow-hidden border border-toot-grid-strong bg-toot-surface-terminal shadow-[0_1.5rem_5rem_rgba(0,0,0,0.52)]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -top-28 h-72 w-72 rounded-full border border-toot-red/25 bg-toot-red/10 blur-[0.5px]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-48 left-1/2 h-96 w-[min(72rem,120vw)] -translate-x-1/2 rounded-t-full border border-toot-red/20 opacity-80"
        />
        {hasBrand || hasNav ? (
          <div
            className={cn(
              "relative z-10 grid gap-px bg-toot-grid-strong/70",
              hasBrand && hasNav && "lg:grid-cols-[minmax(19rem,0.95fr)_minmax(0,1.7fr)]",
            )}
          >
            {hasBrand ? (
              <section className="bg-toot-black/90 p-6 sm:p-8 lg:p-9">
                {logo ? (
                  <Link
                    aria-label="Go to home page"
                    className="mb-7 flex w-fit flex-wrap items-center gap-4 rounded-[var(--radius-toot-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
                    href="/"
                  >
                    <span className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-[var(--radius-toot-panel)] border border-toot-grid bg-card p-2 shadow-sm">
                      <Image
                        alt={logo.alternateText}
                        className="h-full w-full"
                        height={logo.height ?? undefined}
                        imageClassName="h-full w-full !object-contain"
                        sizes={logo.sizes ?? undefined}
                        src={logo.defaultSrc}
                        srcSet={logo.srcSet ?? undefined}
                        style={logoStyle}
                        width={logo.width ?? undefined}
                      />
                    </span>
                    {logoCaption ? (
                      <span className="text-3xl font-black uppercase leading-none tracking-[-0.06em] text-toot-red">
                        {logoCaption}
                      </span>
                    ) : null}
                  </Link>
                ) : null}
                {description ? (
                  <div className="max-w-xl text-base leading-7 text-toot-muted [overflow-wrap:anywhere] sm:text-lg sm:leading-8 lg:max-w-md">
                    {description}
                  </div>
                ) : null}
                {derivedContactSnippets.length > 0 ? (
                  <dl className="mt-7 grid gap-px overflow-hidden border border-toot-grid bg-toot-grid">
                    {derivedContactSnippets.map((snippet, index) => (
                      <div
                        className="min-w-0 bg-toot-surface-terminal p-4"
                        key={`${snippet.label}-${index}`}
                      >
                        <dt className="text-[0.65rem] font-extrabold uppercase tracking-[0.18em] text-toot-orange">
                          {snippet.label}
                        </dt>
                        <dd className="mt-2 min-w-0 text-sm font-semibold leading-5 text-toot-foreground [overflow-wrap:anywhere]">
                          {snippet.href ? (
                            <Link
                              className="transition-colors hover:text-toot-orange"
                              href={snippet.href}
                            >
                              {snippet.value}
                            </Link>
                          ) : (
                            snippet.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </section>
            ) : null}
            {hasNav ? (
              <nav
                aria-label="Footer"
                className={cn(
                  "grid gap-px bg-toot-grid-strong/70 sm:grid-cols-2",
                  footerGroups.length > 2 && "xl:grid-cols-3",
                )}
              >
                {footerGroups.map((group, index) => (
                  <section
                    className="bg-toot-black/90 p-6 sm:p-8 lg:p-9"
                    key={`${group.header}-${index}`}
                  >
                    <div className="mb-5 flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="mt-1.5 h-2 w-2 shrink-0 bg-toot-red shadow-[0_0_1rem_rgba(255,77,46,0.72)]"
                      />
                      <div className="min-w-0">
                        <h2 className="text-sm font-black uppercase tracking-[0.16em] text-toot-foreground">
                          {group.header}
                        </h2>
                      </div>
                    </div>
                    <ul className="grid gap-1">
                      {group.links.map((link, linkIndex) => (
                        <li key={`${link.text}-${link.href}-${linkIndex}`}>
                          <FooterLink link={link} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                {needsTwoColumnFiller ? (
                  <div
                    aria-hidden="true"
                    className={cn(
                      "hidden bg-toot-black/90 sm:block",
                      footerGroups.length > 2 && "xl:hidden",
                    )}
                  />
                ) : null}
                {threeColumnEmptyCells > 0 ? (
                  <div
                    aria-hidden="true"
                    className={cn(
                      "hidden bg-toot-black/90 xl:block",
                      threeColumnEmptyCells === 2 && "xl:col-span-2",
                    )}
                  />
                ) : null}
              </nav>
            ) : null}
          </div>
        ) : null}

        {hasLower ? (
          <div className="relative z-10 grid gap-px bg-toot-grid-strong/70 lg:grid-cols-[1fr_auto]">
            <div className="flex flex-col gap-5 bg-toot-black/95 p-6 text-sm leading-6 text-toot-muted sm:p-8 xl:flex-row xl:items-center xl:justify-between">
              {copyright ? <div>{copyright}</div> : null}
              {footerLegalLinks.length > 0 ? (
                <nav aria-label="Legal">
                  <ul className="flex flex-wrap gap-x-4 gap-y-2">
                    {footerLegalLinks.map((link, index) => (
                      <li key={`${link.text}-${link.href}-${index}`}>
                        <FooterLink className="text-[0.68rem]" link={link} />
                      </li>
                    ))}
                  </ul>
                </nav>
              ) : null}
            </div>
            {footerSocialLinks.length > 0 ? (
              <nav aria-label="Social" className="bg-toot-black/95 p-6 sm:p-8">
                <ul className="flex flex-wrap gap-3">
                  {footerSocialLinks.map((link, index) => (
                    <li key={`${link.text}-${link.href}-${index}`}>
                      <FooterLink
                        className="border border-toot-grid-strong bg-toot-surface-terminal px-4 text-toot-foreground hover:border-toot-orange hover:bg-secondary"
                        link={link}
                      />
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}
          </div>
        ) : null}

        <div aria-hidden="true" className="relative z-10 h-16 overflow-hidden bg-toot-black/95">
          <div className="absolute inset-x-0 top-0 h-px bg-toot-grid-strong/70" />
          <div className="absolute left-1/2 top-6 h-44 w-[min(56rem,92vw)] -translate-x-1/2 rounded-t-full border border-toot-red/25" />
          <div className="absolute left-1/2 top-10 h-32 w-[min(38rem,78vw)] -translate-x-1/2 rounded-t-full border border-toot-orange/15" />
        </div>
      </div>
    </div>
  );
};

TootFooterNav.displayName = "Navigation::TootFooterNav";
